<template>
  <div class="agent-documents index-ignore">
    <button v-for="doc in documents" :key="doc.id" type="button" aria-haspopup="dialog" @click="open(doc)">
      {{ doc.title }}<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 2.5h8l4 4v11H4zM12 2.5v4h4M7 10h6M7 13h6" /></svg>
    </button>
    <Teleport to="body">
      <dialog ref="dialog" class="agent-document-dialog" :aria-label="selected?.title || 'Agent documentation'" @close="onClose" @click="closeBackdrop" @keydown.stop>
        <header class="agent-document-header">
          <div><h2>{{ selected?.title }}</h2><span>{{ selected?.path }}</span></div>
          <button type="button" aria-label="Close document" autofocus @click="close"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg></button>
        </header>
        <div ref="content" class="agent-document-content" tabindex="0" :aria-label="selected?.title">
          <p v-if="loading" role="status">Loading document…</p>
          <div v-else-if="error" role="status"><p>This document could not be loaded.</p><button type="button" @click="load">Try again</button></div>
          <pre v-else>{{ text }}</pre>
        </div>
      </dialog>
    </Teleport>
  </div>
</template>

<script>
import { lockBodyScroll } from '../../utils/bodyScrollLock';
const documents = [
  { id: 'guide', title: 'Reading guide', path: '/agent/guide.txt' },
  { id: 'archive', title: 'Complete public text', path: '/agent/archive.txt' },
  { id: 'api', title: 'API reference', path: '/agent/openapi.json' },
];
export default {
  data: () => ({ documents, selected: null, loading: false, error: false, text: '' }),
  beforeUnmount() { this._request?.abort(); this._release?.(); this.$refs.dialog?.close(); },
  methods: {
    async open(doc) {
      this._trigger = document.activeElement;
      this.selected = doc;
      this._release ||= lockBodyScroll();
      this.$refs.dialog.showModal();
      await this.load();
    },
    async load() {
      this._request?.abort();
      const request = this._request = new AbortController();
      this.loading = true; this.error = false; this.text = '';
      try {
        const response = await fetch(this.selected.path, { signal: request.signal });
        if (!response.ok) throw new Error('Document unavailable');
        const text = await response.text();
        if (!request.signal.aborted) this.text = text;
      } catch (error) {
        if (!request.signal.aborted) this.error = true;
      } finally {
        if (!request.signal.aborted) { this.loading = false; this.$refs.content.scrollTop = 0; }
      }
    },
    close() { this.$refs.dialog.close(); },
    closeBackdrop(event) { if (event.target === this.$refs.dialog) this.close(); },
    onClose() {
      this._request?.abort(); this._release?.(); this._release = null;
      this.text = ''; this.selected = null;
      if (this._trigger?.isConnected) this._trigger.focus({ preventScroll: true });
    },
  },
};
</script>

<style scoped>
.agent-documents { display: flex; flex-wrap: wrap; gap: 10px 24px; padding: 1rem 0 .3rem; }
.agent-documents > button { display: inline-flex; align-items: center; gap: 7px; padding: 6px 0; border: 0; background: none; color: var(--case-ink); font: 14px/1.5 'Quattrocento Sans', sans-serif; cursor: var(--site-interactive-cursor, pointer); }
.agent-documents svg { width: 15px; height: 15px; fill: none; stroke: currentColor; stroke-width: 1.2; }
.agent-documents > button:focus-visible { outline: 1px solid currentColor; outline-offset: 4px; }
.agent-document-dialog { --document-paper: #faf9f7; --document-ink: #353430; box-sizing: border-box; width: min(900px, calc(100vw - 32px)); height: min(780px, calc(100dvh - 64px)); max-height: calc(100dvh - 32px); padding: 0; border: 1px solid color-mix(in srgb, var(--document-ink) 15%, transparent); border-radius: 6px; background: var(--document-paper); color: var(--document-ink); }
.agent-document-dialog[open] { display: flex; flex-direction: column; animation: document-enter 180ms ease-out; }
.agent-document-dialog::backdrop { background: rgb(25 24 23 / 30%); backdrop-filter: blur(8px); animation: document-backdrop 180ms ease-out; }
.agent-document-header { display: flex; flex: none; justify-content: space-between; align-items: center; gap: 20px; padding: 20px 26px; border-bottom: 1px solid color-mix(in srgb, currentColor 12%, transparent); }
.agent-document-header h2 { margin: 0; font: 400 22px/1.4 'PT Serif', Georgia, serif; }
.agent-document-header span { display: block; margin-top: 4px; color: var(--site-text-secondary); font: 12px/1.5 ui-monospace, monospace; }
.agent-document-header button { display: grid; place-items: center; flex: none; width: 36px; height: 36px; padding: 8px; border: 0; border-radius: 50%; background: transparent; color: inherit; cursor: var(--site-interactive-cursor, pointer); }
.agent-document-header svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.3; }
.agent-document-content { flex: 1; min-height: 0; padding: 24px 28px; overflow: auto; overscroll-behavior: contain; }
.agent-document-dialog .agent-document-content pre { box-sizing: border-box; width: 100%; margin: 0; padding: 0; background: transparent; color: inherit; white-space: pre-wrap; word-break: normal; overflow-wrap: anywhere; font: 400 13px/1.8 ui-monospace, SFMono-Regular, Consolas, monospace; }
.agent-document-content:focus-visible { outline: 1px solid var(--site-accent); outline-offset: -3px; }
body.dark-mode .agent-document-dialog { --document-paper: #242424; --document-ink: #dedbd6; }
@media (hover: hover) { .agent-documents > button:hover { color: var(--site-accent); }.agent-document-header button:hover { background: color-mix(in srgb, currentColor 7%, transparent); } }
@media (max-width: 600px) { .agent-document-dialog { height: calc(100dvh - 32px); }.agent-document-header { padding: 16px; }.agent-document-header h2 { font-size: 19px; }.agent-document-content { padding: 20px 16px; } }
@keyframes document-enter { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes document-backdrop { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .agent-document-dialog[open], .agent-document-dialog::backdrop { animation: none; } }
</style>
